import React, { useState, useContext, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';

import {
  SelectedItem as SelectedItemCircle,
  SoleProp as SolePropIcon,
  CloseCorp as CloseCorpIcon,
  Company as CompanyIcon,
  NPO as NPOIcon,
  Partnership as PartnershipIcon,
  Trust as TrustIcon,
  buyInstore as buyInstoreIcon,
  buyOnline as buyOnlineIcon,
  AssistantIcon,
} from 'src/assets/images';
import {
  onboardingBusinessDetailsReskinYourBusinessRoute,
  onboardingChooseSolutionRoute,
  onboardingLetsGetStartedRoute,
} from 'src/routing/routes';
import {
  SalesforceContext,
  Steps,
  JourneyType,
  LockedFieldNames,
  CustomerTypes,
} from 'src/context/salesforce-context';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import TealiumEvents from 'src/helpers/tealium-events';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';

import MonthlyTurnoverModal from './monthly-turnover/monthly-turnover';
import NavigationFooter from 'src/components/navigation-footer/navigation-footer';
import { getAcquiringChannels } from 'src/containers/onboarding/helpers/acquiring-channel';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import WebcamChecker from './webcam-checker/webcam-checker';
import { isValidNumber } from 'src/helpers/validation';
import './business-type.scss';

const BUSINESS_TYPE_NAME_TO_KEY_LOOKUP = (() => {
  let result = {};
  for (let key in BUSINESS_TYPE_KEY_TO_NAME_LOOKUP)
    result[BUSINESS_TYPE_KEY_TO_NAME_LOOKUP[key]] = key;
  return result;
})();

const CONSUMER_TYPE_NAME_TO_KEY_LOOKUP = (() => {
  let result = {};
  for (let key in CONSUMER_TYPE) result[CONSUMER_TYPE[key]] = key;
  return result;
})();

const CONSUMER_TYPES = {
  owner: { name: 'Business owner/director', svg_url: SolePropIcon },
  assistant: {
    name: 'Assistant on behalf of owner/director',
    svg_url: AssistantIcon,
  },
};

const BUSINESS_TYPES = {
  sole_prop: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
    icon: 'icon-business-man',
    key: 'sole_prop',
    vodapayChannelAllowed: true,
    svg_url: SolePropIcon,
    requiredDocs: [
      {
        description: ['Bank statement or Bank confirmation letter in PDF or JPEG format'],
        key: 'bank_account',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },

  partnership: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.partnership,
    icon: 'icon-partnership',
    key: 'partnership',
    vodapayChannelAllowed: false,
    svg_url: PartnershipIcon,
    requiredDocs: [
      {
        description: [
          'Bank statement or Bank confirmation letter in PDF or JPEG format',
          'Resolution letter, not older than 3 months',
        ],
        key: 'bank_account1',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },
  company: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.company,
    icon: 'icon-buildings',
    key: 'company',
    vodapayChannelAllowed: true,
    svg_url: CompanyIcon,
    requiredDocs: [
      {
        description: [
          'Bank statement or Bank confirmation letter in PDF or JPEG format',
          'Resolution letter, not older than 3 months',
        ],
        key: 'bank_account',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },
  close_corp: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.close_corp,
    icon: 'icon-buildings',
    key: 'close_corp',
    vodapayChannelAllowed: false,
    svg_url: CloseCorpIcon,
    requiredDocs: [
      {
        description: [
          'Bank statement or Bank confirmation letter in PDF or JPEG format',
          'Resolution letter, not older than 3 months',
        ],
        key: 'bank_account',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },
  non_profit_org: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.non_profit_org,
    icon: 'icon-heart-hands',
    key: 'non_profit_org',
    vodapayChannelAllowed: false,
    svg_url: NPOIcon,
    requiredDocs: [
      {
        description: [
          'Bank statement or Bank confirmation letter in PDF or JPEG format',
          'Resolution letter, not older than 3 months',
          'NPO Founding Documentation',
        ],
        key: 'bank_account',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },
  trust: {
    name: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.trust,
    icon: 'icon-trust',
    key: 'trust',
    vodapayChannelAllowed: false,
    svg_url: TrustIcon,
    requiredDocs: [
      {
        description: [
          'Bank statement or Bank confirmation letter in PDF or JPEG format',
          'Resolution letter, not older than 3 months',
          'Trust Deed',
        ],
        key: 'bank_account',
      },
    ],
    idText: <p>Form of identification such as green ID book or Smart card ID in JPEG format</p>,
  },
};

const getApplicableBusinessTypes = (businessTypes, isVodapayChannel) => {
  if (!isVodapayChannel) {
    return businessTypes;
  }

  return Object.keys(businessTypes).reduce((result, key) => {
    const value = businessTypes[key];
    if (value.vodapayChannelAllowed) {
      result[key] = value;
    }
    return result;
  }, {});
};

export default function BusinessType() {
  const [showTurnoverModal, setShowTurnoverModal] = useState(false);
  const {
    lead,
    setLead,
    save,
    isResumedSession,
    setSolutionType,
    solutionType,
    terminals,
    isLoadingFromSessionStorage,
    customerType,
    isVodapayChannel,
    journeyType,
    setJourneyType,
    noBuyInStoreDevicesExist,
    isHideBuyOnlineDevices,
    isFieldLocked,
    isVodacomBusinessChannel,
  } = useContext(SalesforceContext);
  const [businessTypes] = useState(getApplicableBusinessTypes(BUSINESS_TYPES, isVodapayChannel));
  const topOfPage = useRef('top');

  useEffect(() => {
    if (
      isValidNumber(lead.monthlyCardTurnover) &&
      lead.monthlyCardTurnover >= 0 &&
      !isVodacomBusinessChannel
    ) {
      /* for the vodacom business channel, pre-setup applications already have
         monthly turnover set, so we don't want to trigger this save on page load. */
      save(Steps.MONTHLY_CARD_TURNOVER);
    }
  }, [lead.monthlyCardTurnover]);

  const textTable = {
    dealer: {
      title: 'What type of business does the customer own?',
      mobileSubtitle:
        'In order for us to provide you with the correct requirements to fulfil the application please let us know what type of business your customer owns?',
      requiredDocs: 'Please have the following ready to complete the application',
      requiredDocsSubtext: 'Your documents must be saved on your device or on your cloud storage',
      journeyText:
        'Did the customer buy their device in store or is the customer looking applying online?',
      customerTypeTitle: 'Who will be applying for this?',
    },
    agent: {
      title: 'What type of business does the customer own?',
      mobileSubtitle:
        'In order for us to provide you with the correct requirements to fulfil the application please let us know what type of business your customer owns?',
      requiredDocs: 'Please have the following ready to complete the application',
      requiredDocsSubtext:
        "The documents must be saved on the customer's device or on cloud storage",
      journeyText:
        'Did the customer buy their device in store or is the customer looking applying online?',
      customerTypeTitle: 'Who will be applying for this?',
    },
    normal: {
      title: 'What type of business do you own?',
      mobileSubtitle:
        'In order for us to provide you with the correct requirements to fulfil your application please let us know what type of business you own?',
      requiredDocs: 'Please have the following ready to complete your application',
      requiredDocsSubtext: 'The documents must be saved on your device or on cloud storage',
      journeyText: 'Are you activating an existing POS device?',
      customerTypeTitle: 'Who will be applying for this?',
    },
    pos: {
      title: 'What type of business do you own?',
      mobileSubtitle:
        'In order for us to provide you with the correct requirements to fulfil your application please let us know what type of business you own?',
      requiredDocs: 'Please have the following ready to complete your application',
      requiredDocsSubtext: 'The documents must be saved on your device or on cloud storage',
      journeyText: 'Are you activating an existing POS device?',
      customerTypeTitle: 'Who will be applying for this?',
    },
  };

  const buyingOptionSelected = () => solutionType && solutionType.length > 0;

  const businessTypeSelected = () => {
    return BUSINESS_TYPE_NAME_TO_KEY_LOOKUP[
      !!lead.typeOfBusiness ? lead.typeOfBusiness : BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop
    ];
  };
  const setBusinessTypeSelected = (businessTypeSelected) => {
    setLead({
      ...lead,
      typeOfBusiness: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP[businessTypeSelected],
    });
  };

  const consumerTypeSelected = () =>
    !!lead.consumerType ? CONSUMER_TYPE_NAME_TO_KEY_LOOKUP[lead.consumerType] : '';

  const setConsumerTypeSelected = (consumerType) =>
    setLead({
      ...lead,
      consumerType: CONSUMER_TYPE[consumerType],
    });

  const monthlyCardTurnover = () => {
    return lead.monthlyCardTurnover || 0;
  };

  const setMonthlyCardTurnover = (monthlyCardTurnover) => {
    setLead({ ...lead, monthlyCardTurnover: monthlyCardTurnover });
  };

  useEffect(() => {
    if (!isLoadingFromSessionStorage) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.businessTypePageLoad(isResumedSession, customerType, assistantId);
      if (topOfPage.current) {
        topOfPage.current.scrollIntoView(false);
      }
      if (!isResumedSession) {
        toastMessageServiceSingleton.addToast({
          message: `We sent an email to ${lead.email}`,
          type: 'info',
        });
      }
    }
  }, [isLoadingFromSessionStorage]);

  const hideJourneyTypeSelectionButtons = () =>
    noBuyInStoreDevicesExist(terminals) ||
    isVodapayChannel ||
    isVodacomBusinessChannel ||
    customerType === CustomerTypes.agent;

  const hideBusinessTypeSelectionButtons = () =>
    isFieldLocked(LockedFieldNames.LEAD_TYPE_OF_BUSINESS);

  useEffect(() => {
    if (customerType === CustomerTypes.agent) {
      setJourneyType(JourneyType.buyOrRentOnlineFicaOnline);
    }
    if (!solutionType) {
      setSolutionType(
        getAcquiringChannels(
          customerType,
          journeyType,
          lead.typeOfBusiness,
          isHideBuyOnlineDevices,
          noBuyInStoreDevicesExist(terminals),
          isVodapayChannel,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (!isLoadingFromSessionStorage) {
      setSolutionType(
        getAcquiringChannels(
          customerType,
          journeyType,
          lead.typeOfBusiness,
          isHideBuyOnlineDevices,
          noBuyInStoreDevicesExist(terminals),
          isVodapayChannel,
        ),
      );
    }
  }, [customerType, journeyType, lead.typeOfBusiness, isLoadingFromSessionStorage]);

  const [redirect, setRedirect] = useState();

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  const handleBack = () => {
    save(Steps.BUSINESS_TYPE);
    setRedirect(onboardingLetsGetStartedRoute);
  };

  const handleNext = () => {
    TealiumEvents.businessTypePageNextClick(
      businessTypeSelected(),
      consumerTypeSelected(),
      journeyType,
      customerType,
    );
    const assistantId = lead.dealerId || lead.agentId || '';
    TealiumEvents.businessTypePageJourneyPhaseCalculateFees(customerType, assistantId);
    save(Steps.BUSINESS_TYPE);
    setShowTurnoverModal(true);
  };

  const onSave = () => {
    save(Steps.BUSINESS_TYPE);
  };

  return (
    <div className="BusinessType is-fullheight" data-testid="BusinessType">
      <MonthlyTurnoverModal
        show={showTurnoverModal}
        onHide={() => setShowTurnoverModal(false)}
        onNext={async (values, doCleanup) => {
          if (isVodacomBusinessChannel) {
            /* need to block here as opportunity in local state needs to be setup
              so that correct quantities appear on next screen
             */
            await save(Steps.MONTHLY_CARD_TURNOVER, null, false, {
              ...lead,
              monthlyCardTurnover: values.turnover,
            });
          }

          await doCleanup();
          setShowTurnoverModal(false);
          TealiumEvents.letsCalculateYourFeesClickCalculate(
            values.unknownTurnover ? 0 : values.turnover,
            customerType,
          );

          if (isVodapayChannel) {
            setRedirect(onboardingBusinessDetailsReskinYourBusinessRoute);
          } else {
            setRedirect(onboardingChooseSolutionRoute);
          }
        }}
        setMonthlyCardTurnover={setMonthlyCardTurnover}
        monthlyCardTurnover={monthlyCardTurnover}
        consumerType={lead.consumerType}
        customerType={customerType}
        isTurnoverReadOnly={isFieldLocked(LockedFieldNames.LEAD_MONTHLY_CARD_TURNOVER)}
      />
      <WebcamChecker />
      <div className="has-margin-top-3" ref={topOfPage}>
        {!hideJourneyTypeSelectionButtons() && (
          <>
            <h4 className="heading-four has-text-centered has-padding-bottom-4 has-mobile-padding-bottom-6 has-padding-x-1 has-margin-x-4 color-whitegrey">
              {textTable[customerType].journeyText}
            </h4>
            <div className="option-selection is-mobile is-center">
              <div
                automationid={`Onboarding_Button_BusinessType`}
                className="card-padding"
                onClick={() => setJourneyType(JourneyType.buyInStoreFicaOnline)}
              >
                <div className="icon-wrapper">
                  <div className="Icon_Box">
                    <div
                      automationid={`Onboarding_Button_BusinessTypeName`}
                      className={`has-padding-y-7 has-padding-x-6 square-selectable-item ${
                        journeyType === JourneyType.buyInStoreFicaOnline ? 'active' : ''
                      }`}
                    >
                      <div
                        automationid={`Onboarding_Button_BusinessTypeIcon`}
                        className={`middle-icon`}
                      >
                        <img className="journeyType" src={buyInstoreIcon} />
                      </div>
                      <p className="block-text">I want to activate a card machine I purchased</p>
                    </div>
                  </div>
                  <div className="selectionBox">
                    {journeyType === JourneyType.buyInStoreFicaOnline ? (
                      <img src={SelectedItemCircle} />
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
              <div
                automationid={`Onboarding_Button_BusinessType`}
                className="card-padding"
                onClick={() => {
                  setJourneyType(JourneyType.buyOrRentOnlineFicaOnline);
                }}
              >
                <div className="icon-wrapper">
                  <div className="Icon_Box">
                    <div
                      automationid={`Onboarding_Button_BusinessTypeName`}
                      className={`has-padding-y-7 has-padding-x-6 square-selectable-item ${
                        journeyType === JourneyType.buyOrRentOnlineFicaOnline ? 'active' : ''
                      }`}
                    >
                      <div
                        automationid={`Onboarding_Button_BusinessTypeIcon`}
                        className={`middle-icon`}
                      >
                        <img className="journeyType" src={buyOnlineIcon} />
                      </div>
                      <p className="block-text">I want to apply for a VodaPay product</p>
                    </div>
                  </div>
                  <div className="selectionBox">
                    {journeyType === JourneyType.buyOrRentOnlineFicaOnline ? (
                      <img src={SelectedItemCircle} />
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {((hideJourneyTypeSelectionButtons() && solutionType) || !!journeyType) && (
          <>
            {!hideBusinessTypeSelectionButtons() && (
              <>
                <h4 className="heading-four has-text-centered has-padding-bottom-1 has-mobile-padding-bottom-6 has-padding-x-1 has-margin-x-4 color-whitegrey has-padding-top-4">
                  {textTable[customerType].title}
                </h4>
                <div className="is-hidden-tablet-only is-hidden-desktop-only is-hidden-widescreen-only is-hidden-fullhd">
                  <h4 className="subtext has-text-centered has-mobile-padding-bottom-3 color-bluegrey3 has-padding-x-4 has-margin-bottom-0">
                    {textTable[customerType].mobileSubtitle}
                  </h4>
                </div>
                <div className="option-selection is-mobile">
                  {Object.keys(businessTypes).map((key) => (
                    <div
                      automationid={`Onboarding_Button_BusinessType_${key}`}
                      key={key}
                      className="card-padding"
                      onClick={() => setBusinessTypeSelected(key)}
                    >
                      <div className="icon-wrapper">
                        <div className="Icon_Box">
                          <div
                            automationid={`Onboarding_Button_BusinessTypeName_${key}`}
                            className={`has-padding-y-7 has-padding-x-6 square-selectable-item ${
                              key === businessTypeSelected() ? 'active' : ''
                            }`}
                          >
                            <div
                              automationid={`Onboarding_Button_BusinessTypeIcon_${key}`}
                              className={`middle-icon`}
                            >
                              <img src={businessTypes[key].svg_url} />{' '}
                            </div>

                            <p className="block-text">{businessTypes[key].name}</p>
                          </div>
                        </div>

                        <div className={`selectionBox`}>
                          {businessTypes[businessTypeSelected()].name ===
                          businessTypes[key].name ? (
                            <img src={SelectedItemCircle} />
                          ) : (
                            ''
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <>
              <h4 className="heading-four has-text-centered has-padding-bottom-1 has-mobile-padding-bottom-6 has-padding-x-1 has-margin-x-4 color-whitegrey has-padding-top-4">
                {textTable[customerType].customerTypeTitle}
              </h4>
              <div className="is-hidden-tablet-only is-hidden-desktop-only is-hidden-widescreen-only is-hidden-fullhd">
                <h4 className="subtext has-text-centered has-mobile-padding-bottom-3 color-bluegrey3 has-padding-x-4 has-margin-bottom-0">
                  {textTable[customerType].customerTypeTitle}
                </h4>
              </div>
              <div className="option-selection is-mobile is-center">
                {Object.keys(CONSUMER_TYPES).map((key) => (
                  <div
                    automationid={`Onboarding_Button_ConsumerType_${key}`}
                    key={key}
                    className="card-padding"
                    onClick={() => setConsumerTypeSelected(key)}
                  >
                    <div className="icon-wrapper">
                      <div className="Icon_Box">
                        <div
                          automationid={`Onboarding_Button_ConsumerTypeName_${key}`}
                          className={`has-padding-y-7 has-padding-x-6 square-selectable-item ${
                            key === consumerTypeSelected() ? 'active' : ''
                          }`}
                        >
                          <div
                            automationid={`Onboarding_Button_ConsumerTypeIcon_${key}`}
                            className={`middle-icon`}
                          >
                            <img src={CONSUMER_TYPES[key].svg_url} />{' '}
                          </div>

                          <p className="block-text">{CONSUMER_TYPES[key].name}</p>
                        </div>
                      </div>

                      <div className={`selectionBox`}>
                        {!!consumerTypeSelected() &&
                        CONSUMER_TYPES[consumerTypeSelected()].name === CONSUMER_TYPES[key].name ? (
                          <img src={SelectedItemCircle} />
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>

            <div className="heading-four color-bluegrey1 has-padding-top-1 has-mobile-margin-top-8 has-padding-x-4 has-text-centered has-padding-bottom-5 has-mobile-padding-3">
              {textTable[customerType].requiredDocs}
            </div>
            <div className="required-docs-subtext has-text-centered has-padding-bottom-3 has-mobile-padding-x-6">
              {textTable[customerType].requiredDocsSubtext}
            </div>

            <div className="option-selection document-options has-padding-x-2 has-padding-bottom-4 has-mobile-padding-x-6">
              <div
                className="document-item"
                automationid={`Onboarding_Div_BusinessTypeRequirement_id`}
              >
                <div className="img-wrapper_id"></div>
                <div className="document_wrapper">
                  <div className="document-item-heading">Identification</div>
                  <div className="document-item-text">
                    {businessTypes[businessTypeSelected()].idText}
                  </div>
                </div>
              </div>

              {businessTypes[businessTypeSelected()] &&
                businessTypes[businessTypeSelected()].requiredDocs.map((doc) => (
                  <div
                    key={doc.key}
                    className="document-item"
                    automationid={`Onboarding_Div_BusinessTypeRequirement_${doc.key}`}
                  >
                    <div className="img-wrapper_documents"></div>
                    <div className="document_wrapper">
                      <div className="document-item-heading">Documentation</div>
                      <div className="content">
                        <ul>
                          {doc.description.map((res, index) => (
                            <li key={index} className="document-item-text no-pad">
                              {res}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              <div
                className="document-item"
                automationid={`Onboarding_Div_BusinessTypeRequirement_selfie`}
              >
                <div className="img-wrapper_camera"></div>

                <div className="document_wrapper">
                  <div className="document-item-heading">Photo of yourself</div>

                  <div className="document-item-text">A camera or webcam to take a selfie</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NavigationFooter
        onBack={handleBack}
        onNext={handleNext}
        onSave={onSave}
        hideCancel={true}
        nextEnabled={businessTypeSelected() && buyingOptionSelected() && consumerTypeSelected()}
        automationIdPrefix="Onboarding"
        hideBack={isVodacomBusinessChannel}
      />
    </div>
  );
}
